import { Flex } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { Icons } from 'ui'

import { useMemberCanReadGettingStarted } from '@/app/roles/api/selectors'
import { HOME_SIDEBAR_TRANSITION } from '@/common/constants/sidebar'
import { useIsWfNotifications } from '@/common/hooks/feature-flags/useIsWfNotifications'
import { usePreviewMode } from '@/common/hooks/usePreviewMode'
import { WithNotifications } from '@/modules/notifications/components/WithNotifications'

import { SidebarHeader } from '../components/SidebarHeader'
import { SidebarNavItem } from '../components/SidebarNavItem'
import { SidebarProfile } from '../components/SidebarProfile'
import { useSidebarOptions } from '../hooks/useSidebarOptions'
import { SidebarLayout } from '../layouts/SidebarLayout'

const transitionDurationMatch = HOME_SIDEBAR_TRANSITION.match(/(\d+)ms/)
const transitionDuration = transitionDurationMatch
	? parseInt(transitionDurationMatch[1], 10)
	: 250

export const HomeSidebarPartial = () => {
	const [isHovered, setIsHovered] = useState(false)
	const [isFullyExpanded, setIsFullyExpanded] = useState(false)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const options = useSidebarOptions()
	const isPreview = usePreviewMode()
	const isWfNotificationsEnabled = useIsWfNotifications()
	const hasGettingStartedAccess = useMemberCanReadGettingStarted()
	const location = useLocation()

	const isPathActive = (path: string) => {
		return !!matchPath(
			{ path, end: false, caseSensitive: false },
			location.pathname,
		)
	}

	const handleItemMouseEnter = () => {
		setIsHovered(true)
	}

	useEffect(() => {
		if (isHovered) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			timeoutRef.current = setTimeout(() => {
				setIsFullyExpanded(true)
			}, transitionDuration)
		} else {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
				timeoutRef.current = null
			}
			setIsFullyExpanded(false)
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [isHovered])

	if (isPreview) {
		return null
	}

	return (
		<SidebarLayout isHovered={isHovered} setIsHovered={setIsHovered}>
			<Flex direction="column" w="100%" alignItems="center" px={2}>
				<SidebarHeader
					isHovered={isHovered}
					isFullyExpanded={isFullyExpanded}
					onMouseEnter={handleItemMouseEnter}
				/>
				<Flex w="100%" direction="column" gap={1} mt={4}>
					{isWfNotificationsEnabled && (
						<WithNotifications>
							{({ isOpen, setIsOpen, unreadCount }) => (
								<SidebarNavItem
									title="Notifications"
									isActive={isOpen}
									isHovered={isHovered}
									isFullyExpanded={isFullyExpanded}
									navTo="/notifications"
									icon={Icons.bell}
									badge={unreadCount}
									onClick={() => {
										setIsOpen(!isOpen)
									}}
									onMouseEnter={handleItemMouseEnter}
								/>
							)}
						</WithNotifications>
					)}
					{options.map((option) => {
						const hasActiveSubroute = option.suboptions?.some(
							(sub) => isPathActive(sub.navTo) || sub.isActive,
						)

						return (
							<SidebarNavItem
								key={option.title}
								isHovered={isHovered}
								isFullyExpanded={isFullyExpanded}
								onMouseEnter={handleItemMouseEnter}
								isActive={option.isActive || hasActiveSubroute}
								{...option}
							/>
						)
					})}
				</Flex>
			</Flex>

			<Flex direction="column" gap={4} pb={3} px={2}>
				<Flex w="full" direction="column" gap={1}>
					<SidebarNavItem
						title="Docs & Help Center"
						isHovered={isHovered}
						isFullyExpanded={isFullyExpanded}
						navTo="https://docs.default.com/"
						isNewTab
						icon={Icons.books}
						onMouseEnter={handleItemMouseEnter}
					/>
					{hasGettingStartedAccess && (
						<SidebarNavItem
							title="Getting Started"
							isHovered={isHovered}
							isFullyExpanded={isFullyExpanded}
							navTo="/guide"
							isActive={isPathActive('guide')}
							icon={Icons.guide}
							onMouseEnter={handleItemMouseEnter}
						/>
					)}
					<SidebarNavItem
						title="Profile & Settings"
						isHovered={isHovered}
						isFullyExpanded={isFullyExpanded}
						navTo="settings/user/profile"
						isActive={isPathActive('settings')}
						icon={Icons.shine}
						onMouseEnter={handleItemMouseEnter}
					/>
				</Flex>
				<SidebarProfile
					isHovered={isHovered}
					isFullyExpanded={isFullyExpanded}
					onMouseEnter={handleItemMouseEnter}
				/>
			</Flex>
		</SidebarLayout>
	)
}
